import { useMemo } from "react";
import { useApp } from "@/contexts/AppContext";
import { useScenarios } from "@/hooks/useScenarios";
import type { ComprehensiveScenario } from "@/types/scenarios";
import { usePermissions } from "./usePermissions";

// Workflow stage assignees — mirrors ApprovalWorkflowsPage demo data
// Stage 1 assignee: Linda Kowalski (id '4' / cfo), Stage 2 assignee: Catherine Hughes (id '1' / executive)
const WORKFLOW_STAGE_ASSIGNEES: Record<string, string[]> = {
  "under-review": ["4"], // Stage 1 — Linda Kowalski
  "pending-approval": ["1"], // Stage 2 — Catherine Hughes
};

/**
 * Returns only the scenarios the current user is permitted to see.
 *
 * Visibility rules (any one must be true):
 *  1. Creator      — createdBy === currentUser.id
 *  2. Explicit share — shares[] contains an entry for this user
 *                      (applies to all statuses including 'draft')
 *  3. Workflow assignee — user is assigned to the current workflow stage
 *                         AND status is 'under-review' or 'pending-approval'
 *  4. Active broadcast — status is 'active' (visible to all users with view_scenarios)
 *  5. System admin — isSystemAdmin (full audit visibility)
 */
export function useVisibleScenarios(): ComprehensiveScenario[] {
  const { scenarios } = useScenarios();
  const { currentUser, mode } = useApp();
  const { hasPermission, isSystemAdmin } = usePermissions();

  return useMemo(() => {
    // In live mode the backend already filters scenarios by authorization
    if (mode === "live") return scenarios;

    // ── Demo-mode visibility rules ──────────────────────────────────────
    // Rule 5 — System admin sees everything
    if (isSystemAdmin) return scenarios;

    const userId = currentUser.id;

    return scenarios.filter((scenario) => {
      // Rule 1 — Creator always sees their own scenarios
      if (scenario.createdBy === userId) return true;

      const hasShare = scenario.shares.some((s) => s.userId === userId);
      const status = scenario.status;

      switch (status) {
        case "draft":
          return hasShare;

        case "under-review":
        case "pending-approval": {
          const stageAssignees = WORKFLOW_STAGE_ASSIGNEES[status];
          return hasShare || (stageAssignees?.includes(userId) ?? false);
        }

        case "rejected":
          return hasShare;

        case "active":
          return true; // Active scenarios visible to all users with view_scenarios

        case "parked":
        case "archived":
          return hasShare || hasPermission("view_parked_scenarios");

        default:
          return false;
      }
    });
  }, [scenarios, currentUser.id, mode, hasPermission, isSystemAdmin]);
}
